import { create } from "zustand";

export interface CartItem {
  rowId: string;
  productId: string;
  productName: string;
  unitPrice: number;
  taxRatePercent: number;
  quantity: number;
  paymentMethodId: string | null;
  note?: string;
}

interface CartState {
  items: CartItem[];
  discount: number;
  guestCount: number;
  paymentMethodId: string | null;
  tableId: string | null;
  tableName: string;
  note: string;
  idempotencyKey: string;
  addItem: (product: { id: string; name: string; priceIncludingTax: number; taxRatePercent: number }) => void;
  incrementItem: (rowId: string) => void;
  decrementItem: (rowId: string) => void;
  removeItem: (rowId: string) => void;
  setItems: (items: CartItem[]) => void;
  setDiscount: (value: number) => void;
  setGuestCount: (value: number) => void;
  setPaymentMethodId: (id: string | null) => void;
  setItemPaymentMethodId: (rowId: string, paymentMethodId: string) => void;
  setTableId: (tableId: string | null) => void;
  setTableName: (tableName: string) => void;
  setNote: (note: string) => void;
  clearItems: () => void;
  clear: () => void;
  regenerateIdempotencyKey: () => void;
}

/**
 * crypto.randomUUID()はセキュアコンテキスト（HTTPSまたはlocalhost）でしか使えない。
 * 本アプリは店舗内LANでのHTTP配信を前提としているため、サーバー機以外の端末
 * （iPadやスマホ）からアクセスすると常に非セキュアコンテキストになり例外を投げる。
 * crypto.getRandomValues()はセキュアコンテキスト要件がないため、こちらでUUID v4を組み立てる。
 */
export function generateId(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const useCartStore = create<CartState>((set) => ({
  items: [],
  discount: 0,
  guestCount: 1,
  paymentMethodId: null,
  tableId: null,
  tableName: "",
  note: "",
  idempotencyKey: generateId(),
  addItem: (product) =>
    set((state) => {
      // 同じ商品でも支払方法や単価が異なる行は別行として扱う（会計を支払方法で分割したり、
      // チップ等の金額自由入力商品を毎回別の金額で追加したりできるようにするため）。
      // 現在の既定の支払方法・同じ単価の行があればそこに数量を足し、なければ新しい行を追加する。
      const existing = state.items.find(
        (item) =>
          item.productId === product.id &&
          item.paymentMethodId === state.paymentMethodId &&
          item.unitPrice === product.priceIncludingTax,
      );
      if (existing) {
        return {
          items: state.items.map((item) =>
            item.rowId === existing.rowId ? { ...item, quantity: item.quantity + 1 } : item,
          ),
        };
      }
      return {
        items: [
          ...state.items,
          {
            rowId: generateId(),
            productId: product.id,
            productName: product.name,
            unitPrice: product.priceIncludingTax,
            taxRatePercent: product.taxRatePercent,
            quantity: 1,
            paymentMethodId: state.paymentMethodId,
          },
        ],
      };
    }),
  incrementItem: (rowId) =>
    set((state) => ({
      items: state.items.map((item) => (item.rowId === rowId ? { ...item, quantity: item.quantity + 1 } : item)),
    })),
  decrementItem: (rowId) =>
    set((state) => ({
      items: state.items
        .map((item) => (item.rowId === rowId ? { ...item, quantity: item.quantity - 1 } : item))
        .filter((item) => item.quantity > 0),
    })),
  removeItem: (rowId) => set((state) => ({ items: state.items.filter((item) => item.rowId !== rowId) })),
  setItems: (items) => set({ items }),
  setDiscount: (value) => set({ discount: Math.max(0, Math.trunc(value)) }),
  setGuestCount: (value) => set({ guestCount: Math.max(1, Math.trunc(value)) }),
  setPaymentMethodId: (id) =>
    set((state) => ({
      paymentMethodId: id,
      items: state.items.map((item) => ({ ...item, paymentMethodId: id })),
    })),
  setItemPaymentMethodId: (rowId, paymentMethodId) =>
    set((state) => ({
      items: state.items.map((item) => (item.rowId === rowId ? { ...item, paymentMethodId } : item)),
    })),
  setTableId: (tableId) => set({ tableId }),
  setTableName: (tableName) => set({ tableName }),
  setNote: (note) => set({ note }),
  clearItems: () => set({ items: [], discount: 0 }),
  clear: () =>
    set({
      items: [],
      discount: 0,
      guestCount: 1,
      paymentMethodId: null,
      tableId: null,
      tableName: "",
      note: "",
      idempotencyKey: generateId(),
    }),
  regenerateIdempotencyKey: () => set({ idempotencyKey: generateId() }),
}));

export function computeCartTotals(items: CartItem[], discount: number): { subtotal: number; total: number } {
  const subtotal = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const total = Math.max(0, subtotal - discount);
  return { subtotal, total };
}
